import api from './api';
import {
  queueTransaction,
  getPendingTransactions,
  getFailedTransactions,
  getPendingCount,
  getFailedCount,
  markTransactionSynced,
  markTransactionFailed,
  deleteTransaction,
  clearFailedTransactions,
  retryFailedTransactions as dbRetryFailed,
  setupOnlineListener,
} from '../utils/offlineDB';

/**
 * UUID v7 (RFC 9562) time-ordered unique identifier generator.
 * Combines 48-bit Unix timestamp millisecond precision with 74 random bits.
 */
export function generateUUIDv7() {
  const now = Date.now();
  const timeHex = now.toString(16).padStart(12, '0');

  const bytes = new Uint8Array(10);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 10; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  // Version 7: top 4 bits of 7th octet = 0x7 (0x7000..0x7fff)
  const ver = ((0x7 << 12) | (((bytes[0] << 8) | bytes[1]) & 0x0fff)).toString(16).padStart(4, '0');
  // Variant: top 2 bits of 9th octet = 10 (0x8000..0xbfff) per RFC 4122/9562
  const variant = (((0x80 | (bytes[2] & 0x3f)) << 8) | bytes[3]).toString(16).padStart(4, '0');

  const rest = Array.from(bytes.slice(4))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  return `${timeHex.slice(0, 8)}-${timeHex.slice(8, 12)}-${ver}-${variant}-${rest}`;
}

/**
 * Compute exponential backoff delay with random jitter.
 * delay = min(maxDelay, baseDelay * 2^retryCount) + jitter
 */
export function computeBackoffDelay(retryCount, baseDelay = 1000, maxDelay = 30000) {
  const exp = Math.min(Math.max(0, retryCount), 6);
  const base = Math.min(maxDelay, baseDelay * Math.pow(2, exp));
  const jitter = Math.floor(Math.random() * (base * 0.25));
  return base + jitter;
}

/**
 * Distinguish between transient retryable network/server errors vs permanent 4xx client errors.
 */
export function isRetryableError(error) {
  if (!error) return true;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true;
  if (error.message === 'Network Error' || error.code === 'ECONNABORTED' || error.message === 'Offline mode') {
    return true;
  }
  if (error.response) {
    const status = error.response.status;
    // 408 Request Timeout, 429 Rate Limit, 5xx Server Errors are retryable
    if (status === 408 || status === 429 || status >= 500) return true;
    // Client errors like 400 Bad Request, 401, 403, 404, 409, 422 are non-retryable dead-letter items
    return false;
  }
  return true;
}

/* ------------------------------------------------------------------ *
 *  State & Listener Observer Bus
 * ------------------------------------------------------------------ */
let isSyncing = false;
let lastSyncTime = null;
const listeners = new Set();

function notifyListeners() {
  const status = getSyncStatus();
  listeners.forEach((fn) => {
    try {
      fn(status);
    } catch (e) {
      // Ignore listener error
    }
  });
}

export function subscribeToSyncStatus(listener) {
  listeners.add(listener);
  // Initial callback
  listener(getSyncStatus());
  return () => listeners.delete(listener);
}

export function getSyncStatus() {
  const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
  return {
    isOnline: online,
    isSyncing,
    lastSyncTime,
  };
}

/* ------------------------------------------------------------------ *
 *  Queue & Transaction Operations
 * ------------------------------------------------------------------ */
/**
 * Save a transaction offline with UUID v7 idempotency key and client transaction ID.
 */
export const saveOfflineTransaction = async (transactionData, options = {}) => {
  try {
    const idempotencyKey = transactionData.idempotencyKey || generateUUIDv7();
    const clientTransactionId = transactionData.clientTransactionId || generateUUIDv7();

    const enrichedData = {
      ...transactionData,
      idempotencyKey,
      clientTransactionId,
      offlineTimestamp: Date.now(),
    };

    const id = await queueTransaction({
      type: options.type || 'sale',
      data: enrichedData,
      idempotencyKey,
      clientTransactionId,
      metadata: {
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'node',
        source: options.source || 'pos_checkout',
        ...options.metadata,
      },
    });

    notifyListeners();

    // Attempt to register background sync if Service Worker SyncManager is available
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('sync-pos-transactions');
      } catch (err) {
        console.warn('[Offline] Background sync registration skipped:', err);
      }
    }

    return {
      success: true,
      id,
      idempotencyKey,
      clientTransactionId,
    };
  } catch (error) {
    console.error('[Offline] Error saving transaction:', error);
    return false;
  }
};

/**
 * Synchronize offline pending transactions to the backend server with idempotency & backoff.
 */
export const syncOfflineTransactions = async () => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    console.log('[Offline] Cannot sync, still offline');
    notifyListeners();
    return { success: false, reason: 'offline' };
  }

  if (isSyncing) {
    console.log('[Offline] Sync already in progress');
    return { success: false, reason: 'in_progress' };
  }

  isSyncing = true;
  notifyListeners();

  try {
    const transactions = await getPendingTransactions();
    if (!transactions || transactions.length === 0) {
      isSyncing = false;
      lastSyncTime = Date.now();
      notifyListeners();
      return { success: true, syncedCount: 0, total: 0 };
    }

    console.log(`[Offline] Processing ${transactions.length} offline transactions...`);

    let successCount = 0;
    let failedCount = 0;

    for (const tx of transactions) {
      const idempotencyKey = tx.idempotencyKey || tx.data?.idempotencyKey || generateUUIDv7();

      try {
        // Post transaction with idempotency header
        await api.post('/sales', tx.data, {
          headers: {
            'Idempotency-Key': idempotencyKey,
            'X-Client-Transaction-ID': tx.clientTransactionId || idempotencyKey,
          },
        });

        // Mark synced and delete from local queue
        await markTransactionSynced(tx.id);
        await deleteTransaction(tx.id);
        successCount++;
      } catch (error) {
        console.error(`[Offline] Failed to sync transaction ${tx.id}:`, error);

        if (isRetryableError(error)) {
          // Increment retry count with exponential backoff annotation
          await markTransactionFailed(tx.id, error.message || 'Retryable network failure');
          // If offline or complete network drop, halt batch processing
          if (typeof navigator !== 'undefined' && !navigator.onLine) {
            break;
          }
        } else {
          // Permanent 4xx client failure: mark as failed dead-letter without blocking other transactions
          failedCount++;
          await markTransactionFailed(tx.id, `Unrecoverable error: ${error.message}`);
        }
      }
    }

    lastSyncTime = Date.now();
    isSyncing = false;
    notifyListeners();

    console.log(`[Offline] Sync finished. Synced: ${successCount}, Failed: ${failedCount}`);
    return {
      success: successCount === transactions.length,
      syncedCount: successCount,
      failedCount,
      total: transactions.length,
    };
  } catch (error) {
    console.error('[Offline] Fatal sync error:', error);
    isSyncing = false;
    notifyListeners();
    return { success: false, error: error.message };
  }
};

/**
 * Retry all previously failed dead-letter transactions
 */
export const retryFailed = async () => {
  const count = await dbRetryFailed();
  notifyListeners();
  if (count > 0 && typeof navigator !== 'undefined' && navigator.onLine) {
    return syncOfflineTransactions();
  }
  return { retriedCount: count };
};

/**
 * Clear all failed dead-letter transactions
 */
export const clearFailed = async () => {
  await clearFailedTransactions();
  notifyListeners();
};

/* ------------------------------------------------------------------ *
 *  Automatic Event Listeners
 * ------------------------------------------------------------------ */
if (typeof window !== 'undefined') {
  setupOnlineListener(
    () => {
      console.log('[Offline] Network restored, triggering auto-sync...');
      notifyListeners();
      syncOfflineTransactions();
    },
    () => {
      console.log('[Offline] Network lost, operating in offline queue mode.');
      notifyListeners();
    }
  );

  // Listen for sync messages from the Service Worker
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'SYNC_TRANSACTIONS') {
        console.log('[Offline] Service worker requested sync');
        syncOfflineTransactions();
      }
    });
  }

  // Attempt sync on startup if already online
  if (navigator.onLine) {
    setTimeout(() => {
      syncOfflineTransactions();
    }, 2000);
  }
}

export default {
  saveOfflineTransaction,
  syncOfflineTransactions,
  generateUUIDv7,
  computeBackoffDelay,
  isRetryableError,
  subscribeToSyncStatus,
  getSyncStatus,
  retryFailed,
  clearFailed,
};
